import { create } from "zustand";
import { devtools } from "zustand/middleware";

// ============================================================================
// TYPES
// ============================================================================

export type NotificationVariant = "success" | "error" | "warning" | "info";
export type ToastPosition =
  | "top-left"
  | "top-center"
  | "top-right"
  | "center-left"
  | "center"
  | "center-right"
  | "bottom-left"
  | "bottom-center"
  | "bottom-right";

export interface ToastAction {
  label: string;
  onClick: () => void;
  variant?: "primary" | "secondary" | "ghost";
}

export interface Toast {
  id: string;
  title?: string;
  message: string;
  variant: NotificationVariant;
  duration?: number; // ms, 0 = no auto-dismiss
  position?: ToastPosition;
  actions?: ToastAction[];
  dismissible?: boolean;
  createdAt: number;
}

export interface ModalAction {
  label: string;
  onClick: () => void;
  variant?: "primary" | "secondary" | "ghost" | "danger";
  closeOnClick?: boolean;
}

export interface Modal {
  id: string;
  title: string;
  description: string;
  variant: NotificationVariant;
  actions?: ModalAction[];
  dismissible?: boolean;
  size?: "sm" | "md" | "lg" | "xl" | "full";
  createdAt: number;
}

export interface NotificationConfig {
  maxToasts: number;
  defaultToastDuration: number;
  defaultToastPosition: ToastPosition;
  defaultModalSize: Modal["size"];
  animations: boolean;
}

interface NotificationState {
  // State
  toasts: Toast[];
  modals: Modal[];
  config: NotificationConfig;

  // Toast actions
  showToast: (
    message: string,
    variant?: NotificationVariant,
    options?: Partial<Omit<Toast, "id" | "message" | "variant" | "createdAt">>,
  ) => string;
  hideToast: (id: string) => void;

  // Modal actions
  showModal: (
    title: string,
    description: string,
    variant?: NotificationVariant,
    options?: Partial<
      Omit<Modal, "id" | "title" | "description" | "variant" | "createdAt">
    >,
  ) => string;
  hideModal: (id: string) => void;

  // Global actions
  hideAll: () => void;
  updateConfig: (config: Partial<NotificationConfig>) => void;

  // Internal
  _removeExpiredToast: (id: string) => void;
}

// ============================================================================
// HELPERS
// ============================================================================

function generateId(prefix: "toast" | "modal"): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

// ============================================================================
// STORE
// ============================================================================

export const useNotificationStore = create<NotificationState>()(
  devtools(
    (set, get) => ({
      // Initial state
      toasts: [],
      modals: [],
      config: {
        maxToasts: 5,
        defaultToastDuration: 5000,
        defaultToastPosition: "bottom-right",
        defaultModalSize: "md",
        animations: true,
      },

      // Toast actions
      showToast: (message, variant = "info", options = {}) => {
        // Suprimir toasts que não sejam de erro
        // Apenas toasts de erro devem ser exibidos
        if (variant !== "error") {
          return "toast-suppressed";
        }

        const id = generateId("toast");
        const { config } = get();

        const newToast: Toast = {
          id,
          message,
          variant,
          duration: options.duration ?? config.defaultToastDuration,
          position: options.position ?? config.defaultToastPosition,
          actions: options.actions,
          dismissible: options.dismissible ?? true,
          title: options.title,
          createdAt: Date.now(),
        };

        set((state) => {
          // Limit max toasts - remove oldest if exceeded
          const updatedToasts = [...state.toasts, newToast];
          if (updatedToasts.length > config.maxToasts) {
            updatedToasts.shift(); // Remove oldest
          }

          return { toasts: updatedToasts };
        });

        // Auto-dismiss if duration > 0
        if (newToast.duration && newToast.duration > 0) {
          setTimeout(() => {
            get()._removeExpiredToast(id);
          }, newToast.duration);
        }

        return id;
      },

      hideToast: (id) => {
        set((state) => ({
          toasts: state.toasts.filter((toast) => toast.id !== id),
        }));
      },

      // Modal actions
      showModal: (title, description, variant = "info", options = {}) => {
        const id = generateId("modal");
        const { config } = get();

        const newModal: Modal = {
          id,
          title,
          description,
          variant,
          actions: options.actions,
          dismissible: options.dismissible ?? true,
          size: options.size ?? config.defaultModalSize,
          createdAt: Date.now(),
        };

        set((state) => ({
          modals: [...state.modals, newModal],
        }));

        return id;
      },

      hideModal: (id) => {
        set((state) => ({
          modals: state.modals.filter((modal) => modal.id !== id),
        }));
      },

      // Global actions
      hideAll: () => {
        set({ toasts: [], modals: [] });
      },

      updateConfig: (newConfig) => {
        set((state) => ({
          config: { ...state.config, ...newConfig },
        }));
      },

      // Internal
      _removeExpiredToast: (id) => {
        const { toasts } = get();
        const toast = toasts.find((t) => t.id === id);

        // Only remove if still exists (user might have dismissed manually)
        if (toast) {
          get().hideToast(id);
        }
      },
    }),
    { name: "NotificationStore" },
  ),
);

// ============================================================================
// HOOK (Re-export for convenience)
// ============================================================================

export function useNotification(): Pick<
  NotificationState,
  | "showToast"
  | "hideToast"
  | "showModal"
  | "hideModal"
  | "hideAll"
  | "updateConfig"
> {
  const state = useNotificationStore();
  return {
    showToast: state.showToast,
    hideToast: state.hideToast,
    showModal: state.showModal,
    hideModal: state.hideModal,
    hideAll: state.hideAll,
    updateConfig: state.updateConfig,
  };
}
